﻿<template>
    <common-page
        :label="ts( 'dev' )" :caption="ts( 'futurePlan' )" :useButtons="false">

        <div class="q-pa-md">
            <q-card flat bordered>
                <q-card-section class="row items-center q-col-gutter-md">
                    <div class="col">
                        <div class="text-h6">{{ ts( 'memoryReport' ) }}</div>
                        <div class="text-caption text-grey-7">
                            <span v-html="ts( 'totalMemSize', { human: total.human, detail: ns( total.bytes ) + ' B' } )" />
                        </div>
                        <div class="q-mt-sm">
                            <q-linear-progress
                                :value="quotaUsage"
                                color="primary"
                                track-color="grey-4"
                                rounded
                                size="10px" />
                            <div class="text-caption text-grey-7 q-mt-xs">
                                {{ ts( 'assumedQuota', { quota: formatBytes( ASSUMED_QUOTA ), usage: ns( quotaUsage, 'percent' ) } ) }}
                            </div>
                        </div>
                    </div>
                    <div class="col-auto row items-center q-gutter-sm">
                        <q-input
                            dense clearable outlined
                            v-model="search"
                            :placeholder="ts( 'keyPrefixFilter' )"
                            style="min-width: 260px">
                            <template #append>
                                <q-icon name="search" />
                            </template>
                        </q-input>

                        <q-btn
                            color="primary" outline icon="refresh" :label="ts( 'refresh' )" @click="refresh"
                            :loading="loading" />
                        <q-btn color="secondary" outline icon="content_copy" :label="ts( 'copyJson' )" @click="copyJson" />
                    </div>
                </q-card-section>

                <q-separator />

                <q-card-section>
                    <q-tabs v-model="tab" dense align="left" active-color="primary" narrow-indicator>
                        <q-tab name="keys" :label="ts( 'keys' )" icon="vpn_key" />
                        <q-tab name="prefixes" :label="ts( 'prefixes' )" icon="view_list" />
                    </q-tabs>

                    <q-separator />

                    <q-tab-panels v-model="tab" animated>
                        <q-tab-panel name="keys">
                            <q-table
                                flat :rows="filteredItems" :columns="itemColumns" row-key="key"
                                :rows-per-page-options="[10, 20, 50, 100, 0]" :pagination="initialPagination" binary-state-sort
                                v-model:expanded="expanded">
                                <template #body="props">
                                    <q-tr :props="props">
                                        <q-td auto-width>
                                            <q-btn
                                                size="sm" round dense flat color="primary"
                                                :icon="props.expand ? 'expand_less' : 'expand_more'"
                                                @click="props.expand = !props.expand"
                                                :title="props.expand ? ts( 'hideContent' ) : ts( 'showContent' )"
                                            />
                                        </q-td>

                                        <q-td key="key" :props="props">
                                            <code>{{ props.row.key }}</code>
                                        </q-td>

                                        <q-td key="total" :props="props" class="text-right">
                                            {{ formatBytes( props.row.totalBytes ) }}
                                        </q-td>

                                        <q-td key="value" :props="props" class="text-right">
                                            {{ formatBytes( props.row.valueBytes ) }}
                                        </q-td>

                                        <q-td key="keySize" :props="props" class="text-right">
                                            {{ formatBytes( props.row.keyBytes ) }}
                                        </q-td>

                                        <q-td key="actions" :props="props" class="text-right">
                                            <q-btn
                                                dense flat color="negative" icon="delete"
                                                @click="removeKey(props.row.key)"
                                                :title="ts( 'removeKey', { key: props.row.key } )"
                                            />
                                        </q-td>
                                    </q-tr>

                                    <q-tr v-show="props.expand" :props="props">
                                        <q-td :colspan="props.cols.length" class="bg-grey-1">
                                            <div class="row items-center justify-between q-gutter-sm">
                                                <div class="col">
                                                    <div class="text-subtitle2">{{ ts( 'keyContent' ) }}</div>
                                                    <div class="text-caption text-grey-7">
                                                        {{ props.row.key }} â€˘ {{ formatBytes( props.row.valueBytes ) }}
                                                    </div>
                                                </div>
                                                <div class="col-auto row items-center q-gutter-sm">
                                                    <q-toggle
                                                        v-model="showRaw[props.row.key]" :label="showRaw[props.row.key] ? ts( 'hide' ) : ts( 'show' )"
                                                        color="primary"
                                                    />
                                                    <q-btn
                                                        dense color="secondary" icon="content_copy" :label="ts( 'copyValue' )" outline
                                                        @click="copyValue(props.row.key)"
                                                    />
                                                    <q-btn
                                                        dense color="secondary" icon="data_object" :label="ts( 'copyJson' )" outline
                                                        :disable="!tryParse(props.row.key).ok"
                                                        @click="copyJsonValue(props.row.key)"
                                                    />
                                                </div>
                                            </div>

                                            <q-separator class="q-my-sm" />

                                            <q-scroll-area style="height: 40vh; max-height: 40vh">
							 				    <pre v-if="showRaw[props.row.key]" class="q-pa-sm bg-white"
                                                     style="white-space: pre-wrap; word-break: break-word;">
{{ prettyOf( props.row.key ) }}
											    </pre>
                                                <div v-else class="q-pa-sm text-grey-7">
                                                    {{ ts( 'hiddenValueHint' ) }}
                                                </div>
                                            </q-scroll-area>
                                        </q-td>
                                    </q-tr>
                                </template>

                                <template #no-data>
                                    <div class="full-width row items-center justify-center q-gutter-sm q-pa-md">
                                        <q-icon name="inbox" size="md" color="grey-6" />
                                        <span class="text-grey-7">{{ ts( 'noData' ) }}</span>
                                    </div>
                                </template>
                            </q-table>
                        </q-tab-panel>

                        <q-tab-panel name="prefixes">
                            <q-table
                                flat :rows="filteredGroups" :columns="groupColumns" row-key="baseKey"
                                :rows-per-page-options="[10, 20, 50, 100, 0]" :pagination="initialPagination" binary-state-sort>
                                <template #body-cell-total="props">
                                    <q-td :props="props">
                                        {{ formatBytes( props.row.bytes ) }}
                                    </q-td>
                                </template>
                                <template #body-cell-keys="props">
                                    <q-td :props="props">
                                        {{ props.row.keys.length }}
                                    </q-td>
                                </template>
                                <template #no-data>
                                    <div class="full-width row items-center justify-center q-gutter-sm q-pa-md">
                                        <q-icon name="inbox" size="md" color="grey-6" />
                                        <span class="text-grey-7">{{ ts( 'noData' ) }}</span>
                                    </div>
                                </template>
                            </q-table>
                        </q-tab-panel>
                    </q-tab-panels>
                </q-card-section>
            </q-card>
        </div>
    </common-page>
</template>

<script setup lang="ts">
import CommonPage from '@parts/commonPage.vue'
import { ns, ts } from '@/plugins/i18nFormat'
import destr from "destr"
import {
    analyzeLocalStorage,
    formatBytes,
    byteLen
} from '@/utils/storageReport'
import type {
    AnalyzeResult,
    KeySizeRow,
    GroupRow,
    AnalyzeOptions
} from '@/utils/storageReport'
import { ref, reactive, computed, onMounted } from 'vue'
import { useQuasar } from 'quasar'

const quasar = useQuasar()
const tab = ref<'keys' | 'prefixes'>( 'keys' )
const loading = ref( false )
const search = ref( '' )
const expanded = ref<any[]>( [] )
const showRaw = reactive<Record<string, boolean>>( {} )

const result = reactive<AnalyzeResult>( {
    items: [],
    groups: [],
    total: { bytes: 0, human: '0 B' }
} )

const itemColumns = [
    { name: 'expander', label: '', field: () => '', align: 'left', sortable: false },
    { name: 'key', label: ts( 'key' ), field: ( r: KeySizeRow ) => r.key, align: 'left', sortable: true },
    { name: 'total', label: ts( 'totalBytes' ), field: ( r: KeySizeRow ) => r.totalBytes, align: 'right', sortable: true },
    { name: 'value', label: ts( 'content' ), field: ( r: KeySizeRow ) => r.valueBytes, align: 'right', sortable: true },
    { name: 'keySize', label: ts( 'keySize' ), field: ( r: KeySizeRow ) => r.keyBytes, align: 'right', sortable: true },
    { name: 'actions', label: '', field: () => '', align: 'right', sortable: false }
]

const groupColumns = [
    { name: 'baseKey', label: ts( 'prefix' ), field: ( r: GroupRow ) => r.baseKey, align: 'left', sortable: true },
    { name: 'total', label: ts( 'content' ), field: ( r: GroupRow ) => r.bytes, align: 'right', sortable: true },
    { name: 'keys', label: ts( 'numberOfKeys' ), field: ( r: GroupRow ) => r.keys.length, align: 'right', sortable: true }
]

const initialPagination = { page: 1, rowsPerPage: 20, sortBy: 'total', descending: true }

const ASSUMED_QUOTA = 5 * 1024 * 1024
const quotaUsage = computed( () => ( ASSUMED_QUOTA ? result.total.bytes / ASSUMED_QUOTA : 0 ) )
const total = computed( () => result.total )

const filteredItems = computed( () => {
    const s = ( search.value || '' ).toLowerCase()
    if ( !s ) return result.items
    return result.items.filter( r => r.key.toLowerCase().includes( s ) )
} )

const filteredGroups = computed( () => {
    const s = ( search.value || '' ).toLowerCase()
    if ( !s ) return result.groups
    return result.groups.filter( g => g.baseKey.toLowerCase().includes( s ) )
} )

async function refresh () {
    loading.value = true
    try {
        analyzeAndSet( {} )

        quasar.notify( { color: 'positive', message: 'Obnoveno', timeout: 1200 } )
    } finally {
        loading.value = false
    }
}

async function safeWriteText ( text: string ): Promise<void> {
    // Try Navigator Clipboard API first
    try {
        if ( typeof navigator !== 'undefined' && navigator.clipboard && navigator.clipboard.writeText ) {
            await navigator.clipboard.writeText( text )
            return
        }
    } catch {
        // fall through to legacy path
    }

    // Legacy fallback using a temporary textarea + execCommand('copy')
    try {
        const el = document.createElement( 'textarea' )
        el.value = text
        el.setAttribute( 'readonly', 'true' )
        el.style.position = 'fixed'
        el.style.left = '-9999px'
        document.body.appendChild( el )
        el.select()
        document.execCommand( 'copy' )
        document.body.removeChild( el )
    } catch ( e ) {
        // Re-throw to let caller handle notification
        throw e
    }
}

async function copyJson () {
    try {
        const payload = {
            total: result.total,
            items: result.items,
            groups: result.groups
        }

        await safeWriteText( JSON.stringify( payload, null, 2 ) )

        quasar.notify( { color: 'positive', icon: 'content_copy', message: 'ZkopĂ­rovĂˇno do schrĂˇnky' } )
    } catch {
        quasar.notify( { color: 'negative', icon: 'error', message: 'NepodaĹ™ilo se zkopĂ­rovat' } )
    }
}

function getValue ( key: string ): string {
    return localStorage.getItem( key ) ?? ''
}

function tryParse ( key: string ): { ok: boolean; json?: unknown } {
    const v = getValue( key )

    if ( !v )
        return { ok: false }

    try {
        return { ok: true, json: destr( v ) }
    } catch {
        return { ok: false }
    }
}

function prettyOf ( key: string ): string {
    const v = getValue( key )

    if ( v === '' )
        return '(' + ts( 'emptyValue' ) + ')'

    const p = tryParse( key )

    if ( p.ok ) {
        try {
            return JSON.stringify( p.json, null, 2 )
        } catch {
            // fallback to raw
        }
    }

    if ( v.length > 200_000 ) {
        return v.slice( 0, 200_000 )
            + "\n\n (" + ts( 'shortcutted', { size: formatBytes( byteLen( v ) - byteLen( v.slice( 0, 200_000 ) ) ) } ) + ")"
    }

    return v
}

async function copyValue ( key: string ) {
    try {
        await safeWriteText( getValue( key ) )

        quasar.notify( { color: 'positive', message: ts( 'valueCopied' ) } )
    } catch {
        quasar.notify( { color: 'negative', message: ts( 'copyValueFailed' ) } )
    }
}

async function copyJsonValue ( key: string ) {
    const p = tryParse( key )

    if ( !p.ok ) {
        quasar.notify( { color: 'warning', message: ts( 'invalidJsonValue' ) } )

        return
    }

    try {
        await safeWriteText( JSON.stringify( p.json, null, 2 ) )

        quasar.notify( { color: 'positive', message: ts( 'jsonCopied' ) } )
    } catch {
        quasar.notify( { color: 'negative', message: ts( 'copyValueFailed' ) } )
    }
}

function removeKey ( key: string ) {
    quasar.dialog( {
        title: ts( 'removeKeyTitle' ),
        message: ts( 'reallyRemoveKey', { key: key } ),
        cancel: true,
        ok: { label: 'Smazat', color: 'negative' }
    } ).onOk( () => {
        try {
            localStorage.removeItem( key )

            delete showRaw[ key ]

            analyzeAndSet( {} )

            quasar.notify( { color: 'positive', message: ts( 'keyRemoved' ) } )
        } catch ( e ) {
            quasar.notify( { color: 'negative', message: ts( 'removeKeyFailed' ) } )
        }
    } )
}

function analyzeAndSet ( options: AnalyzeOptions ) {
    const res = analyzeLocalStorage( options )

    result.items = res.items
    result.groups = res.groups
    result.total = res.total
}

onMounted( () => {
    analyzeAndSet( {} )
} )

// Expose for tests
defineExpose({ copyJson, copyValue, copyJsonValue })
</script>
